// context/AuthContext.js
import React, { createContext, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

export const AuthContext = createContext();

// 🔧 Normalizador de roles (token puede venir en español/mayúsculas)
const normalizeRole = (r) => {
  const s = (r || "").toString().trim().toLowerCase();
  if (["admin", "administrator", "administrador"].includes(s)) return "admin";
  if (["user", "usuario"].includes(s)) return "user";
  if (["client", "cliente", "customer"].includes(s)) return "client";
  return s || null; // si no matchea nada, deja el string normalizado o null
};

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null); // { role, clienteIds, firstName, lastName, userId }

  const readTokenAndSetUser = async () => {
    const token = Cookies.get("access_token"); // asegúrate que el back setea así la cookie
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // Verifica token en backend
      await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/token/verify/`, { token });

      // Decodifica claims
      const payload = jwtDecode(token) || {};
      const roleRaw = payload.role || payload.rol || null;
      const role = normalizeRole(roleRaw);

      const clienteIds =
        payload.clienteIds ||
        (payload.clienteId != null ? [payload.clienteId] : []);

      setUser({
        role, // <-- ya normalizado: 'admin' | 'user' | 'client'
        clienteIds,
        firstName: payload.first_name,
        lastName: payload.last_name,
        userId: payload.user_id,
      });
      setIsAuthenticated(true);
    } catch (e) {
      Cookies.remove("access_token");
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    readTokenAndSetUser();
  }, []);

  // hasRole que también normaliza los argumentos
  const hasRole = (...roles) => {
    if (!user) return false;
    if (!roles || roles.length === 0) return true;
    const wanted = roles.map(normalizeRole);
    return wanted.includes(user.role);
  };

  // Normaliza la ruta a formato "/x"
  const normalizePath = (p = "") => (p.startsWith("/") ? p : `/${p}`);

  // Si es client, acota paths; admin/user ven todo
  const allowedPath = (path) => {
    if (!user) return false;
    const p = normalizePath(path || "");
    if (user.role === "client") {
      return (
        p.startsWith("/ordenes") ||
        p.startsWith("/finanzas") ||
        p.startsWith("/login")
      );
    }
    return true;
  };

  // Para una definición de ruta (routes.js), decide si se puede acceder
  const canAccessRoute = (routeDef) => {
    if (!routeDef) return false;

    // respeta roles de la ruta si existen
    if (routeDef.roles && Array.isArray(routeDef.roles) && routeDef.roles.length) {
      if (!user) return false;
      const required = routeDef.roles.map(normalizeRole);
      if (!required.includes(user.role)) return false;
    }

    // si es protegida, exige login
    if (routeDef.protected && !isAuthenticated) return false;

    // whitelist para client
    if (routeDef.route && !allowedPath(routeDef.route)) return false;

    return true;
    };

  const login = () => readTokenAndSetUser();

  // Limpia el token en TODAS sus variantes (cookie con distintos path/domain y
  // localStorage), porque el interceptor de axios lee de cookie O localStorage.
  const clearAuthCredentials = () => {
    const names = ["access_token", "refresh_token", "token", "refresh"];
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const domains = [undefined, host, `.${host}`];

    names.forEach((name) => {
      Cookies.remove(name);
      Cookies.remove(name, { path: "/" });
      domains.forEach((domain) => {
        Cookies.remove(name, { path: "/", domain });
        // Fallback manual por si path/domain no coinciden con los de js-cookie
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${
          domain ? ` domain=${domain};` : ""
        }`;
      });
      try {
        localStorage.removeItem(name);
      } catch (_) {
        /* noop */
      }
    });
  };

  const logout = () => {
    clearAuthCredentials();
    setIsAuthenticated(false);
    setUser(null);
  };

  const value = useMemo(
    () => ({
      loading,
      isAuthenticated,
      user, // { role: 'admin'|'user'|'client', clienteIds, ... }
      hasRole,
      allowedPath,
      canAccessRoute,
      login,
      logout,
    }),
    [loading, isAuthenticated, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
